import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Input, Label } from 'reactstrap';

import api from 'api/api';
import apiOffers from 'api/offers';
import { MANAGE_OFFER_CHOICES } from 'services/enums/users/permissions';
import translations from 'services/translations/translations';
import useHasPermission from 'services/users/useHasPermission';
import { addMessage } from 'store/messages/actions';

/**
 * Mallipohja: an admin of the choice lists marks the position as a model for new
 * positions, first and on light lime in the position search. Saved at once, apart from
 * the position, so also a position of a sent or ordered offer can be marked.
 */
const PositionTemplateToggle = ({
  offerProductId,
}: {
  offerProductId: number;
}): JSX.Element | null => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.calculationWindow]);
  const isAdmin = useHasPermission(MANAGE_OFFER_CHOICES);
  const [isTemplate, setIsTemplate] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      return undefined;
    }

    let active = true;
    apiOffers.products
      .get(offerProductId)
      .then((position) => {
        if (active) {
          setIsTemplate(position?.isTemplate ?? false);
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [isAdmin, offerProductId]);

  if (!isAdmin || isTemplate === null) {
    return null;
  }

  const toggle = async () => {
    const next = !isTemplate;
    setSaving(true);
    setIsTemplate(next);
    try {
      await api.patch(`/api/v1/offers/products/${offerProductId}/template`, {
        isTemplate: next,
      });
    } catch (error) {
      setIsTemplate(!next);
      dispatch(
        addMessage(t(`${translations.calculationWindow}:template.Save failed`))
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Label
      check
      className="position-template-toggle"
      title={t(`${translations.calculationWindow}:template.Info`)}
    >
      <Input
        checked={isTemplate}
        disabled={saving}
        onChange={toggle}
        type="checkbox"
      />{' '}
      {t(`${translations.calculationWindow}:template.Label`)}
    </Label>
  );
};

export default PositionTemplateToggle;
